import classNames from 'classnames';
import isObject from 'lodash/isObject';
import { memo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { playButtonClickSound } from '@wg/web2clientapi/sound';
import Divider from '@wg/wows-react-uikit/components/Divider/Divider';

import { root } from '~/preloaded';
import settings from '~/settings';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import useMount from '~/hooks/useMount';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentClanIdSelector } from '~/store/selectors/currentAccountSelector';
import { searchResultSelector } from '~/store/selectors/searchSelectors';
import { autocompleteActions } from '~/store/slices/autocompleteSlice';
import { actionsSearch } from '~/Actions/ActionSearch';

import RecommendationsCards from '~/Components/RecommendationsCards/RecommendationsCards';
import StepCSSTransitionGroup from '~/Components/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import SearchResult from '~/UIKit/SearchResult/SearchResult';
import ClanSearchInput from '~/UIKit/components/ClanSearchInput/ClanSearchInput';

import styles from './Search.scss';

import type { ClanSearchAutocompleteItem } from '~/types/declaration';

const Search = () => {
  const searchResult = useAppSelector(searchResultSelector);
  const currentClanId = useAppSelector(currentClanIdSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const removeSearchState = useRef(true);
  const isClanRecHidden =
    searchResult.isFetchedBefore ||
    searchResult.isFetching ||
    searchResult.items.length ||
    (currentClanId && !settings.clanrec2?.showRecommendationsForClansMembers);

  useMount(() => {
    dwhExport.push(DWH_EVENTS.SEARCH.OPEN_PAGE);

    return () => {
      if (removeSearchState.current) {
        dispatch(autocompleteActions.clearAutocompleteState());
        dispatch(actionsSearch.clearSearchState());
      }
    };
  });

  useKeyDown(
    (_, event) => {
      if (!searchResult.isFetchedBefore) {
        return;
      }

      event?.stopPropagation();

      void playButtonClickSound();

      dispatch(autocompleteActions.clearAutocompleteState());
      dispatch(actionsSearch.clearSearchState());
    },
    [KEYS_CODE.ESC],
    true,
  );

  const onClanClick = useCallback(
    (clan: ClanSearchAutocompleteItem | number) => {
      const clanId = String(isObject(clan) ? clan.id : clan);

      dwhExport.push(DWH_EVENTS.SEARCH.OPEN_CLAN, {
        clan_id: clanId,
      });

      removeSearchState.current = false;

      navigate(`${root}${ROUTES_MAP.CLAN_PROFILE.replace(':clanId', clanId)}?source=search`);
    },
    [navigate],
  );

  const renderHeader = () => {
    return (
      <>
        <h2 className={styles.header}>{t('Другие кланы')}</h2>
        <p className={styles.description}>
          {currentClanId
            ? t('Explore other Clans and see how they compare to yours.')
            : t('Use the Recommendation System to find a Clan that best matches your preferences.')}
        </p>
      </>
    );
  };

  const renderSearchInput = () => {
    return (
      <div data-staff-name="search_search-input">
        <ClanSearchInput showIcon={true} onHistoryItemClick={onClanClick} />
      </div>
    );
  };

  const renderSearchResult = () => {
    if (!searchResult.isFetchedBefore) {
      return <Divider type={'major_light'} className={styles.divider} />;
    }

    return (
      <div className={styles.searchResults}>
        <SearchResult onItemClick={onClanClick} />
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <StepCSSTransitionGroup level={1}>
        {renderHeader()}
        <Divider type={'major_light'} className={styles.divider} />
      </StepCSSTransitionGroup>

      <StepCSSTransitionGroup level={2}>
        <h2 className={styles.subHeader}>{t('Поиск кланов')}</h2>
        {renderSearchInput()}
        {renderSearchResult()}
      </StepCSSTransitionGroup>

      <StepCSSTransitionGroup level={3}>
        <div
          className={classNames(styles.clanRec, {
            [styles.clanRecVisible]: !isClanRecHidden,
          })}
        >
          <RecommendationsCards />
        </div>
      </StepCSSTransitionGroup>
    </div>
  );
};

export default memo(Search);
